"use client";

import { useState } from "react";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { Avatar } from "@/components/ui/Avatar";
import { db } from "@/lib/firebase";
import { collection, addDoc } from "firebase/firestore";
import { toTimestamp } from "@/lib/firestore";

interface CreateChatModalProps {
  onClose: () => void;
}

export function CreateChatModal({ onClose }: CreateChatModalProps) {
  const { householdId, members, profile } = useHouseholdContext();
  const t = useTranslations();
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [messageText, setMessageText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Filter out current user from selectable members
  const selectableMembers = members.filter((m) => m.uid !== profile?.uid);

  const toggleMember = (memberId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      selectedMembers.length === 0 ||
      !messageText.trim() ||
      !householdId ||
      !profile?.uid
    )
      return;

    setSubmitting(true);
    setError("");

    try {
      const allMembers = [profile.uid, ...selectedMembers];
      const now = toTimestamp(new Date());

      // Create chat document
      const chatsRef = collection(db, `households/${householdId}/chats`);
      const chatDoc = await addDoc(chatsRef, {
        chat_members: allMembers,
        created_at: now,
        created_by: profile.uid,
        household_id: householdId,
        text: messageText.trim(),
        last_reply_uid: profile.uid,
        last_reply_text: messageText.trim(),
        last_reply_at: now,
      });

      // Create first reply
      const repliesRef = collection(
        db,
        `households/${householdId}/chats/${chatDoc.id}/replies`
      );
      await addDoc(repliesRef, {
        created_by: profile.uid,
        created_at: now,
        text: messageText.trim(),
      });

      onClose();
    } catch (err) {
      console.error("Failed to create chat:", err);
      setError(t("chats.create.errorCreate"));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col text-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("common.cancel")}
          </button>
          <h2 className="font-bold text-base">{t("chats.newChat")}</h2>
          <button
            onClick={handleSubmit}
            disabled={
              selectedMembers.length === 0 ||
              !messageText.trim() ||
              submitting
            }
            className="text-sm font-semibold text-[color:var(--accent-color)] disabled:opacity-50"
          >
            {submitting ? t("chats.create.creating") : t("chats.create.submit")}
          </button>
        </div>

        {/* Member Selection */}
        <div className="p-4 border-b border-gray-100 overflow-y-auto shrink-0">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            {t("chats.create.selectMembers")}
          </p>
          <div className="space-y-1">
            {selectableMembers.map((member) => {
              const isSelected = selectedMembers.includes(member.uid!);
              return (
                <button
                  key={member.uid}
                  onClick={() => toggleMember(member.uid!)}
                  className={`flex items-center gap-3 w-full p-2 rounded-lg transition-colors ${
                    isSelected
                      ? "bg-[color:var(--accent-color)]/10"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <Avatar
                    src={member.profile_url}
                    alt={member.nickname ?? member.name ?? ""}
                    size={36}
                  />
                  <span className="text-sm font-medium flex-1 text-left">
                    {member.nickname ?? member.name ?? t("members.detail.unknownMember")}
                  </span>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? "bg-[color:var(--accent-color)] border-[color:var(--accent-color)]"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Message Input */}
        <form onSubmit={handleSubmit} className="p-4 shrink-0">
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder={t("chats.create.firstMessagePlaceholder")}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-gray-100 text-gray-900 placeholder-gray-400 text-base outline-none resize-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
          />

          {error && (
            <p className="text-red-500 text-sm mt-2">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
}
